import { fetchAdminUserBuilds } from '@/api/admin';
import type { IAdminBuild } from '@/entities/admin-user.entity';
import {
	$buildSawDropperStage,
	$buildSawPayloadEncryptStage,
	selectSawDropperStage,
	selectSawPayloadEncryptStage,
	type IBuildLogLine,
} from '@/entities/build-live.entity';
import BuildCard from '@/components/build/build-card';
import BuildLogViewer from '@/components/build/build-log-viewer';
import BuildStageStepper from '@/components/build/build-stage-stepper';
import { Spinner } from '@/components/ui/spinner';
import { getActiveBuild } from '@/entities/build.entity';
import { useBuildSubscription } from '@/hooks/use-build-subscription';
import { useUnit } from 'effector-react';
import { useCallback, useEffect, useState } from 'react';

interface BuildsTabProps {
	profileId: number;
	readOnly?: boolean;
	fetchBuilds?: (profileId: number) => Promise<IAdminBuild[]>;
	fetchBuildLogs?: (buildId: string) => Promise<{ lines: IBuildLogLine[]; failureMessage?: string }>;
}

export default function BuildsTab({
	profileId,
	readOnly = false,
	fetchBuilds = fetchAdminUserBuilds,
	fetchBuildLogs,
}: BuildsTabProps) {
	const [builds, setBuilds] = useState<IAdminBuild[]>([]);
	const [loading, setLoading] = useState(true);
	const activeBuild = getActiveBuild(builds);
	const { progress: liveProgress, logs } = useBuildSubscription(activeBuild?.id);
	const sawDropperMap = useUnit($buildSawDropperStage);
	const sawPayloadEncryptMap = useUnit($buildSawPayloadEncryptStage);
	const sawDropperStage = selectSawDropperStage(sawDropperMap, activeBuild?.id);
	const sawPayloadEncryptStage = selectSawPayloadEncryptStage(sawPayloadEncryptMap, activeBuild?.id);
	const currentStage = liveProgress?.stage ?? activeBuild?.builderStage ?? null;

	const loadBuilds = useCallback(async () => {
		const data = await fetchBuilds(profileId);
		setBuilds(data);
	}, [profileId, fetchBuilds]);

	useEffect(() => {
		let cancelled = false;
		fetchBuilds(profileId)
			.then(data => {
				if (!cancelled) setBuilds(data);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [profileId, fetchBuilds]);

	if (loading) {
		return (
			<div className='flex justify-center py-8'>
				<Spinner />
			</div>
		);
	}

	if (builds.length === 0) {
		return <div className='text-center text-muted-foreground text-sm py-8'>Билдов нет</div>;
	}

	return (
		<div className='flex flex-col gap-3'>
			{activeBuild ? (
				<div className='rounded-xl border border-chart-5/30 bg-chart-5/10 p-4 flex flex-col gap-3'>
					<div>
						<p className='font-medium text-sm'>Активная сборка</p>
						<p className='text-xs text-muted-foreground'>{activeBuild.appName || activeBuild.id}</p>
					</div>
					<BuildStageStepper
						currentStage={currentStage}
						sawDropperStage={sawDropperStage}
						sawPayloadEncryptStage={sawPayloadEncryptStage}
						skipEncrypt={Boolean(activeBuild.skipEncrypt)}
					/>
					<BuildLogViewer lines={logs} />
				</div>
			) : null}

			{builds.map(build => (
				<BuildCard
					key={build.id}
					build={build}
					adminLogs
					readOnly={readOnly}
					fetchBuildLogs={fetchBuildLogs}
					onInstalled={readOnly ? undefined : () => void loadBuilds()}
				/>
			))}
		</div>
	);
}
